import b from 'b_';
import { isSameWeek } from 'date-fns';
import { ArrowShortBold } from 'education-icons';
import { Button, SimpleCallback, Text, CalendarIntervalDay } from 'platform-components';
import { UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components/src/components/constants';
import React from 'react';
import { useMeasure } from 'react-use';

import { formatDate } from 'utils/date/formatDate';

import './calendar.scss';

export interface Props {
    interval: CalendarIntervalDay[];
    content: Record<string, React.ReactNode[]>;
    onPrevButtonClick: SimpleCallback;
    onNextButtonClick: SimpleCallback;
    progress?: boolean;
    minDate?: Date;
}

const ITEM_WIDTH = 248;
const ITEMS_GAP = 16;

export const Calendar = (props: Props) => {
    const { progress, interval, onPrevButtonClick, onNextButtonClick, content, minDate } = props;
    const [ref, bounds] = useMeasure();
    const columnCount = getColumnCount(ITEM_WIDTH, ITEMS_GAP, bounds.width);
    const disablePrevButton = minDate && Boolean(interval.length) && isSameWeek(new Date(interval[0].date), minDate);

    return (
        <div className={b('calendar', { progress })}>
            <div className="calendar__controls">
                <Button
                    className="calendar__controls-prev"
                    disabled={disablePrevButton}
                    icon={<ArrowShortBold />}
                    onClick={onPrevButtonClick}
                    rounded={true}
                    view={Button.VIEW.RAISED}
                />
                <Button
                    className="calendar__controls-next"
                    icon={<ArrowShortBold />}
                    onClick={onNextButtonClick}
                    rounded={true}
                    view={Button.VIEW.RAISED}
                />
            </div>
            <div className="calendar__days">
                {interval.map(({ date, timestamp }) => {
                    const day = new Date(date);
                    const items = content[date] || [];
                    const columns = new Array(columnCount).fill(true).map(() => [] as React.ReactNode[]);

                    items.forEach((item, index) => {
                        columns[index % columnCount].push(item);
                    });

                    return (
                        // @ts-ignore ref для useMeasure не совпадет с настоящим
                        <div className="calendar__day" key={timestamp} ref={ref}>
                            <div className="calendar__day-header">
                                <Text typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphLMedium}>
                                    {formatDate(day, 'EEEE')}
                                </Text>
                                <Text typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphLMedium}>{formatDate(day, 'd')}</Text>
                            </div>
                            {!progress && (
                                <div className="calendar__day-items">
                                    {columns.map((calendarItems, columnIndex) => (
                                        <div className="calendar__day-items-col" key={columnIndex}>
                                            {calendarItems.map((calendarItem, itemIndex) => (
                                                <div className="calendar__item" key={itemIndex}>
                                                    {calendarItem}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

function getColumnCount(colWidth: number, colGap: number, contentWidth: number) {
    let count = 1;

    while (colWidth * (count + 1) + colGap * count < contentWidth) {
        count += 1;
    }

    return count;
}
